// Models
import { useStores } from 'models';

// React
import { useCallback } from 'react';

// Interfaces
import {
  IAssessmentCancelAttrs,
  IAssessmentGetDetailAttrs,
  IAssessmentGetListAttrs,
  IAssessmentGetProgressDetailAttrs,
  IAssessmentGetAssessmentPhaseListAttrs,
  IAssessmentGetAssessmentStartDetailAttrs,
  IAssessmentUpdateAssessmentStartDetailAttrs,
  IAssessmentSubmitAssessmentStartDetailAttrs,
  IAssessmentDecisionFormListAttrs,
  IAssessmentFilterPhaseListAttrs,
  IAssessmentFilterStatusListAttrs,
  IAssessmentDecisionSubmitAttrs,
} from 'interfaces/back-office/assessment/assessment-view.interface';

// Utils
import { assessmentUtils_isEditable } from 'utils/assessment/assessment.utils';

const useAssessment = () => {
  // Hook
  const {
    assessment: {
      assessment_loading,
      assessment_list,
      assessment_detail,
      assessment_CLEAR_LIST,
      assessment_CLEAR_DETAIL,
      assessment_phase_list,
      start_Assessment_Detail,
      unit_competency_detail,
      filter_phase_list,
      filter_status_list,
      assessment_SET_UNIT_COMPETENCY_DETAIL,
      assessment_SET_ASSESSMENT_PHASE_LIST_LOADING,
      assessment_SET_DECISION_FORM_LIST_LOADING,
      decision_form_list,
      assessment_fetchList: fetchList,
      assessment_fetchProgressDetail: fetchProgressDetail,
      assessment_fetchAssessmentPhaseList: fetchAssessmentPhaseList,
      assessment_fetchDetail: fetchDetail,
      assessment_fetchAssessmentStartDetail: fetchAssessmentStartDetail,
      assessment_fetchFilterPhaseList: fetchFilterPhase,
      assessment_fetchFilterStatusList: fetchFilterStatus,
      assessment_cancel: cancel,
      assessment_updateAssessmentStartDetail: update,
      assessment_sendAssessmentStartDetail: sendAssessment,
      assessment_submitAssessmentPhase: submitAssessment,
      assessment_fetchDecisionFormList: fetchDecisionFormList,
      assessment_submitDecisionPhase: submitDecision,
    },
  } = useStores();

  /**
   * @description Check if assessment editable
   *
   * @return {boolean} boolean
   */
  const assessment_isEditablePhase = useCallback((): boolean => {
    return assessmentUtils_isEditable(assessment_detail.phase!, assessment_detail.status!);
  }, [assessment_detail]);

  /**
   * @description Fetch assessment Phase list
   *
   * @param {IAssessmentGetAssessmentPhaseListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_fetchAssessmentPhaseList = useCallback(
    (payload: IAssessmentGetAssessmentPhaseListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchAssessmentPhaseList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchAssessmentPhaseList],
  );

  /**
   * @description Fetch assessment start detail
   *
   * @param {IAssessmentGetAssessmentStartDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_fetchAssessmentStartDetail = useCallback(
    (payload: IAssessmentGetAssessmentStartDetailAttrs, compId: number): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchAssessmentStartDetail(
          {
            params: { ...payload.params },
            requestConfig: { ...payload.requestConfig },
            onSuccess: resolve,
            onError: reject,
          },
          compId,
        );
      });
    },
    [fetchAssessmentStartDetail],
  );

  /**
   * @description Update assessment start detail
   *
   * @param {IAssessmentUpdateAssessmentStartDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_updateAssessmentStartDetail = useCallback(
    (payload: IAssessmentUpdateAssessmentStartDetailAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        update({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [update],
  );

  /**
   * @description send assessment start detail
   *
   * @param {IAssessmentSubmitAssessmentStartDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_sendAssessmentStartDetail = useCallback(
    (payload: IAssessmentSubmitAssessmentStartDetailAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        sendAssessment({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [sendAssessment],
  );

  /**
   * @description submit assessment start detail
   *
   * @param {IAssessmentSubmitAssessmentStartDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_submitAssessmentPhase = useCallback(
    (payload: IAssessmentSubmitAssessmentStartDetailAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        submitAssessment({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [submitAssessment],
  );

  /**
   * @description submit assessment decision detail
   *
   * @param {IAssessmentSubmitAssessmentStartDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_submitDecisionPhase = useCallback(
    (payload: IAssessmentDecisionSubmitAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        submitDecision({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [submitDecision],
  );

  /**
   * @description fetch decision form list
   *
   * @param {IAssessmentDecisionFormListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_fetchDecisionFormList = useCallback(
    (payload: IAssessmentDecisionFormListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDecisionFormList({
          query: { ...payload.query },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchDecisionFormList],
  );

  /**
   * @description fetch filter phase list
   *
   * @param {IAssessmentFilterPhaseListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_fetchFilterPhaseList = useCallback(
    (payload: IAssessmentFilterPhaseListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchFilterPhase({
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchFilterPhase],
  );

  /**
   * @description fetch filter status list
   *
   * @param {IAssessmentFilterStatusListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_fetchFilterStatusList = useCallback(
    (payload: IAssessmentFilterStatusListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchFilterStatus({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchFilterStatus],
  );

  /**
   * @description Fetch assessment list
   *
   * @param {IAssessmentGetListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_fetchList = useCallback(
    (payload: IAssessmentGetListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchList({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchList],
  );

  /**
   * @description Fetch assessment detail
   *
   * @param {IAssessmentGetProgressDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_fetchProgressDetail = useCallback(
    (payload: IAssessmentGetProgressDetailAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchProgressDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchProgressDetail],
  );

  /**
   * @description Fetch assessment detail
   *
   * @param {IAssessmentGetDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_fetchDetail = useCallback(
    (payload: IAssessmentGetDetailAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [fetchDetail],
  );

  /**
   * @description Cancel assessment
   *
   * @param {IAssessmentCancelAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const assessment_cancel = useCallback(
    (payload: IAssessmentCancelAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        cancel({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    [cancel],
  );

  return {
    assessment_loading,
    assessment_list,
    assessment_phase_list,
    start_Assessment_Detail,
    assessment_detail,
    unit_competency_detail,
    assessment_SET_UNIT_COMPETENCY_DETAIL,
    assessment_SET_ASSESSMENT_PHASE_LIST_LOADING,
    assessment_SET_DECISION_FORM_LIST_LOADING,
    assessment_CLEAR_LIST,
    assessment_CLEAR_DETAIL,
    assessment_fetchList,
    assessment_fetchAssessmentPhaseList,
    assessment_fetchAssessmentStartDetail,
    assessment_fetchProgressDetail,
    assessment_fetchDetail,
    assessment_cancel,
    assessment_isEditablePhase,
    assessment_updateAssessmentStartDetail,
    assessment_sendAssessmentStartDetail,
    assessment_submitAssessmentPhase,
    assessment_submitDecisionPhase,
    assessment_fetchDecisionFormList,
    decision_form_list,
    assessment_fetchFilterPhaseList,
    assessment_fetchFilterStatusList,
    filter_phase_list,
    filter_status_list,
  };
};

export { useAssessment };
